import { EPaper } from '../types/epaper';

type SpecialCategory = 'handbook' | 'review' | 'outlook' | 'special' | 'annual' | 'quarterly';

const SPECIAL_KEYWORDS: { keyword: string; category: SpecialCategory }[] = [
  { keyword: 'handbook', category: 'handbook' },
  { keyword: 'review', category: 'review' },
  { keyword: 'outlook', category: 'outlook' },
  { keyword: 'special', category: 'special' },
  { keyword: 'annual', category: 'annual' },
  { keyword: 'quarterly', category: 'quarterly' },
];

const detectSpecialCategory = (title: string): SpecialCategory | null => {
  const lower = title.toLowerCase();
  return SPECIAL_KEYWORDS.find(({ keyword }) => lower.includes(keyword))?.category ?? null;
};

const sanitizeTitle = (rawTitle: string, isSpecial: boolean): string => {
  let title = rawTitle
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!isSpecial) {
    title = title
      .replace(/^The Brief\s*/i, '')
      .trim();
  }

  return title || rawTitle;
};

export const convertArticlesToEPapers = (articles: any[]): EPaper[] => {
  return articles.map((article, index) => {
    const timestamp = article['@timestamp'];
    const pdfUrl = article['@message'].trim();
    const date = new Date(timestamp).toISOString().split('T')[0];

    const urlParts = pdfUrl.split('/');
    const filename = urlParts[urlParts.length - 1];
    const decodedFilename = decodeURIComponent(filename);
    const rawTitle = decodedFilename
      .replace('_compressed.pdf', '')
      .replace('.pdf', '')
      .replace(/_/g, ' ')
      .replace(/\+/g, ' ');

    const specialCategory = detectSpecialCategory(rawTitle);
    const isSpecial = specialCategory !== null;
    const title = sanitizeTitle(rawTitle, isSpecial);
    const thumbnailUrl = pdfUrl.replace('.pdf', '_thumb.jpg');

    return {
      id: index.toString(),
      title,
      date,
      pdfUrl,
      thumbnailUrl,
      timestamp,
      type: isSpecial ? 'special' : 'regular',
      category: specialCategory
    };
  });
};

export const loadEPapers = async (): Promise<EPaper[]> => {
  const fetchOptions: RequestInit = {
    cache: 'no-cache',
    headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
  };

  try {
    const response = await fetch('https://epaper.thebrief24.com/articles.json', fetchOptions);
    if (response.ok) {
      const articles = await response.json();
      return convertArticlesToEPapers(articles);
    }
  } catch {
    // S3 unavailable — fall through to local
  }

  const localResponse = await fetch('/articles.json');
  if (!localResponse.ok) {
    throw new Error(`Failed to fetch articles.json: ${localResponse.status}`);
  }
  const articles = await localResponse.json();
  return convertArticlesToEPapers(articles);
};
